import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { usePpAccounts } from "../../hooks/usePpAccounts";
import { CdrnMerchantFilterItem } from "../../types/alerts";
import {
  MerchantMappingConfig,
  MerchantMappingSaveBody,
  RemapUnmappedResponse,
  DescriptorMatcher,
  PpType,
} from "../../types/merchantMappingConfig";
import MerchantMappingSettings from "../../components/alerts/MerchantMappingSettings";
import * as Sentry from "@sentry/react";

interface CdrnConfigRaw {
  verifiCdrnAlertsConfigId: number;
  merchantName: string;
  descriptors: string[];
  descriptorMatcher: DescriptorMatcher | null;
  ppType: PpType | null;
  ppAccountId: number | null;
  companyId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function CdrnSettings() {
  const { get, post, put, del, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { ppAccounts } = usePpAccounts();

  const [configs, setConfigs] = useState<CdrnConfigRaw[]>([]);
  const [cdrnMerchants, setCdrnMerchants] = useState<CdrnMerchantFilterItem[]>([]);

  const fetchConfigs = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await get<CdrnConfigRaw[]>(
        `/api/companies/${companyId}/cdrn/merchant-mappings`
      );
      setConfigs(response);
    } catch (err) {
      console.error("Failed to fetch CDRN configs:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchCdrnMerchants = useCallback(async () => {
    if (!companyId) return;
    try {
      const params = `?companyId=${companyId}`;
      const response = await get<{ merchants: CdrnMerchantFilterItem[] }>(
        `/api/alerts/cdrn/merchants${params}`
      );
      setCdrnMerchants(response.merchants);
    } catch (err) {
      console.error("Failed to fetch CDRN merchants:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchCdrnMerchants();
  }, [fetchCdrnMerchants]);

  const merchantNameOptions = useMemo(
    () => [...new Set(cdrnMerchants.map((m) => m.merchant_name))].sort(),
    [cdrnMerchants]
  );

  const sharedConfigs: MerchantMappingConfig[] = useMemo(
    () => configs.map((c) => ({ ...c, configId: c.verifiCdrnAlertsConfigId })),
    [configs]
  );

  const handleCreate = async (body: MerchantMappingSaveBody) => {
    await post(`/api/companies/${companyId}/cdrn/merchant-mappings`, body);
  };

  const handleUpdate = async (configId: number, body: MerchantMappingSaveBody) => {
    await put(`/api/companies/${companyId}/cdrn/merchant-mappings/${configId}`, body);
  };

  const handleDelete = async (configId: number) => {
    await del(`/api/companies/${companyId}/cdrn/merchant-mappings/${configId}`);
  };

  const handleSync = async (): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/cdrn/remap-unmapped`, {}, { slowApi: true });
  };

  const handleSyncCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/cdrn/remap-unmapped`, { company_id: companyId }, { slowApi: true });
  };

  const handleRematchCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/cdrn/remap-unmapped`, { company_id: companyId, mode: 'rematch' }, { slowApi: true });
  };

  return (
    <MerchantMappingSettings
      providerLabel="CDRN"
      merchantFieldLabel="CDRN Merchant Name"
      companyId={companyId}
      configs={sharedConfigs}
      ppAccounts={ppAccounts}
      merchantNameOptions={merchantNameOptions}
      loading={loading}
      apiError={apiError}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onRefresh={fetchConfigs}
      syncFeature={{ onSync: handleSync, onSyncCompany: handleSyncCompany, onRematchCompany: handleRematchCompany }}
    />
  );
}
